import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { isStringArray } from '@/api/queries/utils';
import FiltersContext, { OptionPathsByFilter } from '@/context/FiltersContext/FiltersContext';
import { rehydrateFilterState, getDefaultFiltersState } from '@/context/FiltersContext/FiltersContextProvider';
import { useGetUserCountries } from '@/api/queries/country/useGetUserCountries';
import { UserFilterApiService } from '@/api/userProfile/UserFilterApiService';
import { globalFilters } from '@/models/filter/globalFilterAllowlist';
import { UserFilterMetadata } from '@/models/user/userFilter';
import { useToast } from '@/storybook/components/Toast/Toast';
import { FilterType } from '@/models/filter/FilterType';
import type { FilterState } from '@/types/filter/filterState';
import applyLocalFiltersToStorage from '@/utils/filter/applyLocalFiltersToStorage';
import { AppliedFilterSetStorage } from '@/utils/filter/appliedFilterSetStorage';
import { writeCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import { resolveCountryLabel } from '@/models/filter/utils/countryFilterHelpers';
import { isRecord } from '@/utils/filter/isRecord';
import type { SelectItem } from '@/types/ui/selectItem';
import useSharedViewValidation, { SharedViewScenario } from '@/hooks/useSharedViewValidation';
import { useReconstructOptionPaths } from '@/hooks/useReconstructOptionPaths';
import {
  buildAndTrackErrorDialogState,
  buildSharedViewDialogProps,
  type SharedViewDialogState,
} from '@/hooks/useApplySharedView.dialogState';
import { extractSharedViewResponseFacts } from '@/utils/filter/sharedViewResponseSummary';
import type { SharedViewFailureContext } from '@/utils/filter/sharedViewDiagnostics';
import { validateS3Url } from '@/utils/filter/unwrapS3Response';
import { SharedViewDialogProps } from '@/components/filter/SharedViewDialog/SharedViewDialog';
import { deriveSenderCountryCodesFromLocale, recalculatePresetDateRanges } from './useApplySharedView.utils';
import { migrateSharedViewKeys } from '@/utils/filter/filterKeyMigrations';
import { parseSharedViewPayload } from '@/utils/filter/parseSharedViewPayload';
import { SHARE_VIEW_PARAM } from '@/utils/filter/shareView';

export interface UseApplySharedViewResult {
  isLoading: boolean;
  sharedViewDialogProps: SharedViewDialogProps | null;
}

const useApplySharedView = (): UseApplySharedViewResult => {
  const { t } = useTranslation();
  const { handleApplyFilter, handleClearAllFilters, filterConfig, defaultFiltersState, replaceOptionPaths } =
    useContext(FiltersContext);
  const { makeToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { validateSharedFilters } = useSharedViewValidation();
  const { data: userCountries = [] } = useGetUserCountries<SelectItem<string>[]>();
  const reconstructPaths = useReconstructOptionPaths();
  const isApplying = useRef(false);
  const processedId = useRef<string | null>(null);
  const shareViewId = searchParams.get(SHARE_VIEW_PARAM);
  const [isLoading, setIsLoading] = useState(!!shareViewId);
  const [dialogState, setDialogState] = useState<SharedViewDialogState | null>(null);

  const clearShareViewParam = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete(SHARE_VIEW_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const applyFilters = useCallback(
    (
      filters: FilterState,
      localFiltersData?: Record<string, Record<string, unknown>>,
      filterMetadata?: UserFilterMetadata,
      optionPaths?: OptionPathsByFilter,
      wasModified = false,
    ) => {
      const localFiltersPayload = localFiltersData ?? {};

      const config = filterConfig ?? globalFilters;
      const defaultState = getDefaultFiltersState(config);
      const rehydratedFilters = rehydrateFilterState(filters, defaultState);

      // The registry moves to the shared selection only on a confirmed apply,
      // never during fetch/validation — otherwise a cancelled or errored dialog
      // would leave the paths ahead of the still-applied filter state. A
      // whole-filter replacement without paths clears the registry so a stale
      // hierarchy from a previous selection is not read into the new query.
      if (replaceOptionPaths) {
        replaceOptionPaths(optionPaths ?? {});
      }

      applyLocalFiltersToStorage(localFiltersPayload);
      handleApplyFilter(rehydratedFilters);

      // Re-apply after effects settle (prune/auto-select in page filters)
      setTimeout(() => applyLocalFiltersToStorage(localFiltersPayload), 500);

      if (filterMetadata) {
        AppliedFilterSetStorage.save(filterMetadata, rehydratedFilters, localFiltersPayload);
      }

      makeToast({
        content: String(t(wasModified ? 'global.filters.shareViewModifiedApplied' : 'global.filters.shareViewApplied')),
        appearance: 'success',
        autoClose: 5000,
      });
    },
    [handleApplyFilter, filterConfig, replaceOptionPaths, makeToast, t],
  );

  const handleDialogApply = useCallback(() => {
    if (!dialogState) return;

    if (dialogState.scenario === SharedViewScenario.ERROR) {
      handleClearAllFilters();
    } else {
      const wasModified = dialogState.scenario === SharedViewScenario.VALUES_NOT_FOUND;
      applyFilters(
        dialogState.validation.validFilters,
        dialogState.localFiltersData,
        dialogState.filterMetadata,
        dialogState.optionPaths,
        wasModified,
      );
    }

    setDialogState(null);
    clearShareViewParam();
  }, [dialogState, applyFilters, handleClearAllFilters, clearShareViewParam]);

  const handleDialogCancel = useCallback(() => {
    setDialogState(null);
    clearShareViewParam();
  }, [clearShareViewParam]);

  const applySharedFilters = useCallback(
    async (filterId: string) => {
      if (isApplying.current) return;
      isApplying.current = true;
      setIsLoading(true);
      const failure: SharedViewFailureContext = { stage: 'fetchView', shareViewId: filterId };

      try {
        const response = await UserFilterApiService.getFilterById(filterId);
        Object.assign(failure, extractSharedViewResponseFacts(response));
        const { filters_download_link: downloadLink } = response.data;

        // The auth interceptor is intentionally skipped for pre-signed S3 URLs.
        failure.stage = 'invalidDownloadUrl';
        validateS3Url(downloadLink);

        failure.stage = 'downloadFilters';
        const filtersResponse = await axios.get<Record<string, unknown>>(downloadLink);
        const responseData = filtersResponse.data;

        // Unwrap legacy S3 envelope: { filters: {...}, userId, locale }
        failure.stage = 'invalidPayload';
        failure.rawPayload = responseData;
        const hasFiltersEnvelope = 'filters' in responseData && isRecord(responseData.filters);
        const rawUnwrappedData = hasFiltersEnvelope ? responseData.filters : responseData;

        // Migrate legacy global-filter keys: renamed keys are mapped to their
        // current FilterType value and removed keys are dropped. Without this,
        // views saved by older app versions fail the strict payload guards
        // (surfacing as "Cannot load view") or silently lose renamed filters.
        const unwrappedData = migrateSharedViewKeys(rawUnwrappedData);
        failure.payload = unwrappedData;

        const parsed = parseSharedViewPayload(unwrappedData);
        if (!parsed) {
          throw new Error('Invalid filter payload');
        }

        let rawGlobalFilters = parsed.globalFilters;
        const localFiltersData = parsed.localFilters;
        // Carried through to applyFilters, which moves the registry to these
        // paths only on a confirmed apply (not while a dialog is pending).
        const optionPaths = parsed.optionPaths;
        let senderCountryCodes = parsed.senderCountryCodes ?? [];

        if (parsed.kind === 'structured') {
          if (senderCountryCodes.length === 0 && !parsed.resolvedCountryCodes?.length) {
            senderCountryCodes = deriveSenderCountryCodesFromLocale(response.data.filterCountries ?? '');
          }

          if (parsed.resolvedCountryCodes?.length) {
            writeCachedCountryCodes(parsed.resolvedCountryCodes);
          }
        }

        failure.stage = 'validation';
        // Recalculate preset date ranges (YTD, Monthly, etc.) relative to
        // today so the recipient sees the current period.
        rawGlobalFilters = recalculatePresetDateRanges(rawGlobalFilters);

        // Validate shared filters against recipient's available options
        const validation = await validateSharedFilters(
          rawGlobalFilters,
          senderCountryCodes,
          localFiltersData,
          parsed.resolvedCountryCodes,
        );

        // Shared filters should not be stored as applied filter sets —
        // only saved_filter types show as a filter set chip in the filter bar.
        const metadata = response.data.filter?.filterType === 'saved_filter' ? response.data.filter : undefined;

        const finalPaths = await reconstructPaths(validation.validFilters, optionPaths);

        if (validation.scenario === SharedViewScenario.APPLY) {
          applyFilters(validation.validFilters, validation.validLocalFilters ?? localFiltersData, metadata, finalPaths);
          clearShareViewParam();
        } else {
          const countryFilter = rawGlobalFilters[FilterType.COUNTRY];
          const senderFilterValues = isStringArray(countryFilter) ? countryFilter : undefined;
          setDialogState({
            scenario: validation.scenario,
            validation,
            localFiltersData: validation.validLocalFilters ?? localFiltersData,
            filterMetadata: metadata,
            optionPaths: finalPaths,
            senderCountryLabel: resolveCountryLabel(
              validation.senderCountryCodes,
              userCountries,
              t,
              senderFilterValues,
            ),
            recipientCountryLabel: resolveCountryLabel(validation.recipientCountryCodes, userCountries, t),
          });
        }
      } catch (error) {
        setDialogState(buildAndTrackErrorDialogState(defaultFiltersState, failure, error));
      } finally {
        isApplying.current = false;
        setIsLoading(false);
      }
    },
    [validateSharedFilters, reconstructPaths, applyFilters, defaultFiltersState, userCountries, t, clearShareViewParam],
  );

  useEffect(() => {
    if (shareViewId && processedId.current !== shareViewId) {
      processedId.current = shareViewId;
      applySharedFilters(shareViewId);
    }
  }, [shareViewId, applySharedFilters]);

  const sharedViewDialogProps = dialogState
    ? buildSharedViewDialogProps(dialogState, handleDialogApply, handleDialogCancel, searchParams)
    : null;

  return { isLoading, sharedViewDialogProps };
};

export default useApplySharedView;
